import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Globe, Menu, LogOut, User, Mail, AlertTriangle, RefreshCw } from "lucide-react";
import { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useI18n } from "../i18n/i18n";
import { useAuth } from "./AuthProvider";
import { AuthModal } from "./AuthModal";
import { logout } from "../lib/firebase";
import { useCurrentLanguage, useLanguageNavigate } from "./LanguageRedirect";
import { SUPPORTED_LANGUAGES, LANGUAGE_METADATA, type SupportedLanguage } from "@config/i18n.config";

const Header = () => {
  const location = useLocation();
  const { t } = useI18n();
  const currentLang = useCurrentLanguage();
  const { switchLanguage } = useLanguageNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user, isEmailVerified, refreshUser } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleRefreshUser = async () => {
    setIsRefreshing(true);
    try {
      await refreshUser();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAuthModeChange = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
  };

  const handleLanguageChange = (newLang: SupportedLanguage) => {
    switchLanguage(newLang);
  };

  // Build language-aware navigation paths
  const navItems = [
    { path: `/${currentLang}`, label: t("nav.home") },
    { path: `/${currentLang}/tools`, label: t("nav.tools") },
    { path: `/${currentLang}/resources`, label: t("nav.resources") },
    { path: `/${currentLang}/chat`, label: "Finn" },
    { path: `/${currentLang}/pricing`, label: t("nav.pricing") },
  ];

  // Check if current path matches (accounting for language prefix)
  const isActivePath = (path: string) => location.pathname === path;

  // Determine header background based on current page
  const isSpecialPage = [
    `/${currentLang}`,
    `/${currentLang}/terms`,
    `/${currentLang}/privacy`,
    `/${currentLang}/cookies`,
    `/${currentLang}/chat`
  ].includes(location.pathname);

  return (
    <>
      <header
        className={`sticky top-0 z-50 ${isSpecialPage ? "bg-[#F9FAFB]" : "bg-white"}`}
      >
        <div className="container mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center min-w-0 flex-shrink-0">
              <RouterLink to={`/${currentLang}`}>
                <div className="flex items-center space-x-2 cursor-pointer">
                  <picture>
                    <source type="image/webp" srcSet="/images/brand/logo-32w.webp 32w, /images/brand/logo-64w.webp 64w" sizes="(max-width: 640px) 32px, 40px" />
                    <img 
                      src="/images/brand/logo-32w.png" 
                      srcSet="/images/brand/logo-32w.png 32w, /images/brand/logo-64w.png 64w" 
                      sizes="(max-width: 640px) 32px, 40px"
                      alt="Finanzly Logo" 
                      className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0" 
                      width="40" 
                      height="40" 
                      fetchPriority="high" 
                    />
                  </picture>
                  <h1 className="text-lg sm:text-xl font-bold text-neutral-800 whitespace-nowrap">Finanzly</h1>
                </div>
              </RouterLink>
            </div>

            {/* Desktop Navigation */}
            <NavigationMenu className="hidden md:flex flex-1 justify-center">
              <NavigationMenuList className="flex items-center space-x-4 lg:space-x-8">
                {navItems.map((item) => (
                  <NavigationMenuItem key={item.path}>
                    <RouterLink to={item.path}>
                      <div
                        className={`relative nav-link font-medium cursor-pointer ${
                          isActivePath(item.path)
                            ? "text-[#EA580C] active"
                            : "text-neutral-700 hover:text-[#EA580C]"
                        } transition-colors`}
                      >
                        {item.label}
                      </div>
                    </RouterLink>
                  </NavigationMenuItem>
                ))}

                {/* Authentication Buttons */}
                {user ? (
                  <NavigationMenuItem>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="flex items-center space-x-2 text-neutral-700 hover:text-[#EA580C] transition-colors"
                        >
                          <div className="h-6 w-6 rounded-full bg-[#EA580C] flex items-center justify-center text-white text-sm">
                            {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                          </div>
                          <span className="text-sm font-medium">
                            {user.displayName || user.email?.split('@')[0]}
                          </span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <RouterLink to={`/${currentLang}/profile`}>
                            <User className="mr-2 h-4 w-4" />
                            {t("pricing.profile.title")}
                          </RouterLink>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleLogout}>
                          <LogOut className="mr-2 h-4 w-4" />
                          {t("auth.signOut")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </NavigationMenuItem>
                ) : (
                  <>
                    <NavigationMenuItem>
                      <Button
                        variant="ghost"
                        className="text-neutral-700 hover:text-[#EA580C] transition-colors font-medium"
                        onClick={() => {
                          setAuthMode('login');
                          setAuthModalOpen(true);
                        }}
                      >
                        {t("auth.login")}
                      </Button>
                    </NavigationMenuItem>
                    <NavigationMenuItem>
                      <Button
                        className="bg-gradient-to-r from-orange-400 to-orange-600 text-white hover:from-orange-500 hover:to-orange-700 font-medium px-6 py-2 rounded-full border-0"
                        onClick={() => {
                          setAuthMode('signup');
                          setAuthModalOpen(true);
                        }}
                      >
                        {t("auth.tryForFree")}
                      </Button>
                    </NavigationMenuItem>
                  </>
                )}

                {/* Language Selector */}
                <NavigationMenuItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-neutral-700 hover:text-[#EA580C] transition-colors"
                      >
                        <Globe className="h-4 w-4" />
                        <span>{currentLang.toUpperCase()}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <DropdownMenuItem
                          key={lang}
                          onClick={() => handleLanguageChange(lang)}
                          className={currentLang === lang ? "bg-neutral-100" : ""}
                        >
                          {LANGUAGE_METADATA[lang].nativeName}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Mobile Menu Button */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" className="md:hidden" aria-label="Menu">
                  <Menu className="h-6 w-6 text-neutral-700" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right">
                <div className="flex flex-col space-y-4 pt-4">
                  {navItems.map((item) => (
                    <RouterLink key={item.path} to={item.path}>
                      <div
                        className={`block py-2 px-3 text-base font-medium cursor-pointer ${
                          isActivePath(item.path)
                            ? "text-[#EA580C]"
                            : "text-neutral-700"
                        } hover:bg-neutral-200 rounded-md`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.label}
                      </div>
                    </RouterLink>
                  ))}

                  {/* Mobile Language Selector */}
                  <div className="py-2 px-3">
                    <div className="text-sm font-medium text-neutral-500 mb-2">
                      {t("language.select")}:
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <Button
                          key={lang}
                          variant={currentLang === lang ? "default" : "outline"}
                          className={`text-sm ${
                            currentLang === lang
                              ? "bg-[#EA580C] hover:bg-[#C2410C] text-white"
                              : ""
                          }`}
                          onClick={() => {
                            handleLanguageChange(lang);
                            setIsOpen(false);
                          }}
                        >
                          {LANGUAGE_METADATA[lang].nativeName}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Mobile Authentication */}
                  <div className="py-2 px-3 border-t">
                    {user ? (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 py-2">
                          <div className="h-8 w-8 rounded-full bg-[#EA580C] flex items-center justify-center text-white text-sm">
                            {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                          </div>
                          <span className="text-sm font-medium text-neutral-700">
                            {user.displayName || user.email?.split('@')[0]}
                          </span>
                        </div>
                        <RouterLink to={`/${currentLang}/profile`}>
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => setIsOpen(false)}
                          >
                            <User className="mr-2 h-4 w-4" />
                            {t("pricing.profile.title")}
                          </Button>
                        </RouterLink>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            handleLogout();
                            setIsOpen(false);
                          }}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          {t("auth.signOut")}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            setAuthMode('login');
                            setAuthModalOpen(true);
                            setIsOpen(false);
                          }}
                        >
                          {t("auth.login")}
                        </Button>
                        <Button
                          className="w-full bg-gradient-to-r from-orange-400 to-orange-600 text-white hover:from-orange-500 hover:to-orange-700 font-medium"
                          onClick={() => {
                            setAuthMode('signup');
                            setAuthModalOpen(true);
                            setIsOpen(false);
                          }}
                        >
                          {t("auth.tryForFree")}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <AuthModal
          isOpen={authModalOpen}
          onClose={() => setAuthModalOpen(false)}
          mode={authMode}
          onModeChange={handleAuthModeChange}
        />
      </header>

      {/* Email Verification Banner */}
      {user && !isEmailVerified && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  {t('auth.verification.emailNotVerified')}
                </p>
                <p className="text-xs text-yellow-700">
                  {t('auth.verification.description')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRefreshUser}
                disabled={isRefreshing}
                className="text-yellow-800 hover:bg-yellow-100"
                title="Refresh verification status"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              <RouterLink to={`/${currentLang}/verify-email`}>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-yellow-300 text-yellow-800 hover:bg-yellow-100"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  {t('auth.verification.title')}
                </Button>
              </RouterLink>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
